import { FC } from 'react';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  Grid,
  GridItem,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Progress,
  Spinner,
  Text,
} from '@chakra-ui/react';

import {
  Worker,
  WorkerJob,
  WorkerJobBatch,
  WorkerDetailsResponse,
  WorkerStatus,
  WorkerType,
} from 'api/workers';

interface WorkerDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  worker: Worker | null;
  details?: WorkerDetailsResponse;
  isLoading: boolean;
  isResetting: boolean;
  isResolving: boolean;
  isSkipping: boolean;
  isCancelling: boolean;
  onResetWorker: (workerId: string) => void;
  onResolveWorker: (workerId: string) => void;
  onSkipBatch: (workerId: string, batchId: string) => void;
  onCancelWorker: (workerId: string) => void;
}

const WORKER_TYPE_LABELS: Record<WorkerType, string> = {
  order_delivery: 'Order Delivery',
  create_package: 'Package Creation',
  pickup: 'Pickup',
};

const STATUS_COLORS: Record<WorkerStatus, string> = {
  pending: 'gray',
  processing: 'blue',
  completed: 'green',
  failed: 'red',
  paused: 'orange',
  cancelled: 'gray',
};

const JOB_TYPE_LABELS: Record<string, string> = {
  reserve_cases: 'Reserve Cases',
  reserve_items: 'Reserve Items',
  create_packages: 'Create Packages',
  update_items: 'Update Items',
  update_cases: 'Update Cases',
  update_packages: 'Update Packages',
  create_scan: 'Create Scan',
  recalculate_counts: 'Recalculate Counts',
  post_processing: 'Post Processing',
  post_processing_package_creation: 'Post Processing (Package Creation)',
  update_items_washed: 'Update Items Washed',
  process_unused_packages: 'Process Unused Packages',
  update_packages_returned: 'Update Packages Returned',
  update_cases_returned: 'Update Cases Returned',
  post_processing_pickup: 'Post Processing (Pickup)',
};

const formatDate = (dateString?: string) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleString();
};

const InfoRow: FC<{ label: string; value: string | React.ReactNode }> = ({
  label,
  value,
}) => (
  <Grid templateColumns="200px 1fr" gap={2} py={1}>
    <GridItem>
      <Text fontWeight="semibold" color="gray.600">
        {label}
      </Text>
    </GridItem>
    <GridItem>
      <Text>{value}</Text>
    </GridItem>
  </Grid>
);

const JobCard: FC<{ job: WorkerJob; index: number }> = ({ job, index }) => {
  const progress =
    job.totalBatches > 0 ? (job.completedBatches / job.totalBatches) * 100 : 0;

  return (
    <AccordionItem
      border="1px solid"
      borderColor="gray.200"
      borderRadius="md"
      mb={2}>
      <AccordionButton>
        <Box flex="1" textAlign="left">
          <Flex align="center" gap={2}>
            <Text fontWeight="semibold">
              {index + 1}. {JOB_TYPE_LABELS[job.type] || job.type}
            </Text>
            <Badge colorScheme={STATUS_COLORS[job.status]} size="sm">
              {job.status.toUpperCase()}
            </Badge>
          </Flex>
        </Box>
        <AccordionIcon />
      </AccordionButton>
      <AccordionPanel pb={4}>
        <Progress value={progress} size="sm" colorScheme="green" mb={3} />
        <Grid templateColumns="repeat(2, 1fr)" gap={3} fontSize="sm">
          <GridItem>
            <Text color="gray.600">Batches</Text>
            <Text fontWeight="medium">
              {job.completedBatches} / {job.totalBatches}
            </Text>
          </GridItem>
          <GridItem>
            <Text color="gray.600">Records</Text>
            <Text fontWeight="medium">
              {job.completedRecords} / {job.totalRecords}
            </Text>
          </GridItem>
          <GridItem>
            <Text color="gray.600">Attempts</Text>
            <Text fontWeight="medium">
              {job.attempts} / {job.maxAttempts}
            </Text>
          </GridItem>
          <GridItem>
            <Text color="gray.600">Started</Text>
            <Text fontWeight="medium">{formatDate(job.startedAt)}</Text>
          </GridItem>
          {job.lastError && (
            <GridItem colSpan={2}>
              <Text color="gray.600">Last Error</Text>
              <Text fontWeight="medium" color="red.500" fontSize="xs">
                {job.lastError}
              </Text>
            </GridItem>
          )}
        </Grid>
      </AccordionPanel>
    </AccordionItem>
  );
};

const PausedBatchCard: FC<{
  batch: WorkerJobBatch;
  onSkip?: () => void;
  isSkipping?: boolean;
}> = ({ batch, onSkip, isSkipping }) => (
  <Box
    p={3}
    border="1px solid"
    borderColor="orange.200"
    borderRadius="md"
    bg="orange.50"
    mb={2}>
    <Flex justify="space-between" align="center" mb={2}>
      <Text fontWeight="semibold">Batch #{batch.batchNumber}</Text>
      <Flex align="center" gap={2}>
        <Badge colorScheme="orange">PAUSED</Badge>
        {onSkip && (
          <Button
            size="xs"
            colorScheme="orange"
            variant="outline"
            isLoading={isSkipping}
            onClick={onSkip}>
            Skip
          </Button>
        )}
      </Flex>
    </Flex>
    <Grid templateColumns="repeat(2, 1fr)" gap={2} fontSize="sm">
      <GridItem>
        <Text color="gray.600">Records</Text>
        <Text fontWeight="medium">{batch.recordIds?.length || 0}</Text>
      </GridItem>
      <GridItem>
        <Text color="gray.600">Attempts</Text>
        <Text fontWeight="medium">
          {batch.attempts} / {batch.maxAttempts}
        </Text>
      </GridItem>
      <GridItem>
        <Text color="gray.600">Target Collection</Text>
        <Text fontWeight="medium">{batch.targetCollection}</Text>
      </GridItem>
      <GridItem>
        <Text color="gray.600">Operation</Text>
        <Text fontWeight="medium">{batch.operation?.toUpperCase()}</Text>
      </GridItem>
      {batch.lastError && (
        <GridItem colSpan={2}>
          <Text color="gray.600">Last Error</Text>
          <Text fontWeight="medium" color="red.500" fontSize="xs">
            {batch.lastError}
          </Text>
        </GridItem>
      )}
    </Grid>
  </Box>
);

const WorkerDetailsModal: FC<WorkerDetailsModalProps> = ({
  isOpen,
  onClose,
  worker,
  details,
  isLoading,
  isResetting,
  isResolving,
  isSkipping,
  isCancelling,
  onResetWorker,
  onResolveWorker,
  onSkipBatch,
  onCancelWorker,
}) => {
  if (!worker) return null;

  const isPaused = worker.status === 'paused';
  const workerData = details?.worker || worker;
  const jobs = details?.jobs || [];
  const pausedBatches = details?.pausedBatches || [];

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxW="800px">
        <ModalHeader>
          <Flex align="center" gap={2}>
            <Text>Worker Details</Text>
            <Badge colorScheme={STATUS_COLORS[workerData.status]} fontSize="sm">
              {workerData.status.toUpperCase()}
            </Badge>
          </Flex>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          {isLoading ? (
            <Flex justify="center" py={10}>
              <Spinner size="xl" />
            </Flex>
          ) : (
            <>
              {/* Worker Overview */}
              <Box mb={6}>
                <Heading size="sm" mb={3}>
                  Overview
                </Heading>
                <Box bg="gray.50" p={4} borderRadius="md">
                  <InfoRow
                    label="Type"
                    value={
                      WORKER_TYPE_LABELS[workerData.type] || workerData.type
                    }
                  />
                  <InfoRow
                    label="Progress"
                    value={
                      <Flex align="center" gap={2}>
                        <Progress
                          value={workerData.progress || 0}
                          size="sm"
                          colorScheme="green"
                          flex={1}
                          maxW="200px"
                        />
                        <Text>
                          {(workerData.progress || 0).toFixed(0)}% (
                          {workerData.completedUnits || 0}/
                          {workerData.totalUnits || 0})
                        </Text>
                      </Flex>
                    }
                  />
                  <InfoRow
                    label="Created"
                    value={formatDate(workerData.createdAt)}
                  />
                  <InfoRow
                    label="Updated"
                    value={formatDate(workerData.updatedAt)}
                  />
                  {workerData.startedAt && (
                    <InfoRow
                      label="Started"
                      value={formatDate(workerData.startedAt)}
                    />
                  )}
                  {workerData.completedAt && (
                    <InfoRow
                      label="Completed"
                      value={formatDate(workerData.completedAt)}
                    />
                  )}
                  <InfoRow label="Worker ID" value={workerData._id} />
                </Box>
              </Box>

              {/* Error Section (if paused or has error) */}
              {(isPaused || workerData.error) && (
                <Box mb={6}>
                  <Heading size="sm" mb={3} color="red.500">
                    Error Information
                  </Heading>
                  <Box
                    bg="red.50"
                    p={4}
                    borderRadius="md"
                    border="1px solid"
                    borderColor="red.200">
                    {workerData.error && (
                      <InfoRow
                        label="Error Message"
                        value={
                          <Text color="red.600">
                            {workerData.error.message}
                          </Text>
                        }
                      />
                    )}
                    {workerData.pauseReason && (
                      <InfoRow
                        label="Pause Reason"
                        value={
                          <Text color="orange.600">
                            {workerData.pauseReason}
                          </Text>
                        }
                      />
                    )}
                    {workerData.pausedAt && (
                      <InfoRow
                        label="Paused At"
                        value={formatDate(workerData.pausedAt)}
                      />
                    )}
                    {isPaused && (
                      <Flex mt={4}>
                        <Button
                          colorScheme="orange"
                          onClick={() => onResetWorker(workerData._id)}
                          isLoading={isResetting}>
                          Reset & Resume Worker
                        </Button>
                      </Flex>
                    )}
                  </Box>
                </Box>
              )}

              {/* Paused Batches Section */}
              {pausedBatches.length > 0 && (
                <Box mb={6}>
                  <Heading size="sm" mb={3} color="orange.500">
                    Paused Batches ({pausedBatches.length})
                  </Heading>
                  {pausedBatches.map((batch) => (
                    <PausedBatchCard
                      key={batch._id}
                      batch={batch}
                      onSkip={() => onSkipBatch(workerData._id, batch._id)}
                      isSkipping={isSkipping}
                    />
                  ))}
                </Box>
              )}

              <Divider my={4} />

              {/* Jobs Section */}
              <Box>
                <Heading size="sm" mb={3}>
                  Jobs ({jobs.length})
                </Heading>
                {jobs.length > 0 ? (
                  <Accordion allowMultiple>
                    {jobs.map((job, index) => (
                      <JobCard key={job._id} job={job} index={index} />
                    ))}
                  </Accordion>
                ) : (
                  <Text color="gray.500">No jobs found for this worker.</Text>
                )}
              </Box>
            </>
          )}
        </ModalBody>

        <ModalFooter>
          <Flex gap={3} w="100%" justify="space-between">
            <Flex gap={2}>
              {isPaused && (
                <Button
                  size="sm"
                  colorScheme="orange"
                  isLoading={isResolving}
                  onClick={() => onResolveWorker(workerData._id)}>
                  Resolve (Skip Failed)
                </Button>
              )}
              {workerData.status !== 'completed' &&
                workerData.status !== 'cancelled' && (
                  <Button
                    size="sm"
                    colorScheme="red"
                    variant="outline"
                    isLoading={isCancelling}
                    onClick={() => {
                      if (
                        window.confirm(
                          'Cancel this worker and rollback all changes? This cannot be undone.',
                        )
                      ) {
                        onCancelWorker(workerData._id);
                      }
                    }}>
                    Cancel Worker
                  </Button>
                )}
            </Flex>
            <Button onClick={onClose}>Close</Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default WorkerDetailsModal;
